import { Alert, Box, LinearProgress, Link, TextField, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Button } from '../../../../components/ui';
import FileInput from '../../../../components/Shared/FileUploadPreview/FileInput.jsx';
import useTemplateSx from '../../hooks/useTemplateSx';
import useLeanBulkSend from '../../hooks/useLeanBulkSend';
import { spacingFunction } from '../../../../theme/tokens';

export default function LeanBulkSendPanel({ template, controller }) {
  const styles = useTemplateSx();
  const owned = useLeanBulkSend({ template: controller ? undefined : template });
  const send = controller || owned;
  const counts = send.counts;

  return (
    <Box sx={styles.sendDialogSection}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <UploadFileOutlinedIcon fontSize="small" color="primary" />
        <Typography sx={styles.sendDialogSectionTitle}>Bulk Send via Excel</Typography>
      </Box>
      <Typography sx={styles.sendDialogSectionHint}>
        Upload an Excel file to check it. Messages start only after Send
        {send.scheduledAt ? ' at the scheduled time' : ''}.
      </Typography>

      <TextField
        type="datetime-local"
        size="small"
        label="Schedule send (optional)"
        value={send.scheduledAt || ''}
        onChange={(e) => send.setScheduledAt(e.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={Boolean(send.job) || send.isUploadLoading}
        sx={{
          mt: 1.5,
          mb: 1,
          width: '100%',
          maxWidth: 320,
          '& .MuiOutlinedInput-notchedOutline': {
            top: spacingFunction(0.125),
          },
        }}
      />

      {send.errorRowCount > 0 ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {`${send.errorRowCount} ${send.errorRowCount === 1 ? 'row has' : 'rows have'} errors. Download the file, fix those cells, and upload it again.`}
          <Box sx={{ mt: 1 }}>
            <Button variant="outline" size="sm" onClick={send.downloadErrorFile}>
              Download error file
            </Button>
          </Box>
        </Alert>
      ) : null}
      {send.bulkUploadError ? (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {send.bulkUploadError}
        </Alert>
      ) : null}
      <Box sx={styles.sendDialogBulkRow}>
        <FileInput
          onFileSelect={send.onBulkFileSelect}
          onFileRemove={send.onBulkFileRemove}
          file={send.tempDataFile}
          setFile={send.setTempDataFile}
          accept=".xlsx,.xls"
        />
        {send.tempDataFile ? (
          <Button
            variant="primary"
            size="sm"
            loading={send.isUploadLoading}
            disabled={send.isUploadLoading}
            onClick={send.uploadBulkFile}
          >
            Upload
          </Button>
        ) : null}
      </Box>
      {send.progressBar > 0 ? (
        <Box pt={2}>
          <LinearProgress variant="determinate" value={send.progressBar} />
        </Box>
      ) : null}
      {send.draft && !send.job ? (() => {
        const totalCount = send.draft.total_count ?? (send.draft.queued ?? 0);
        const eligibleCount = send.draft.eligible_count ?? (send.draft.queued ?? 0);
        const optoutCount = send.draft.optout_count ?? 0;
        const duplicatesCount = send.draft.duplicates ?? 0;
        const queuedCount = send.draft.queued ?? eligibleCount;

        return (
          <Alert
            severity={eligibleCount === 0 ? 'warning' : 'info'}
            sx={{
              mt: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1 }}>
              Bulk Upload Summary
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}>
                  Total
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
                  {totalCount}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: '#166534', fontWeight: 500, display: 'block' }}>
                  Eligible
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#15803d' }}>
                  {eligibleCount}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: optoutCount > 0 ? '#fffbeb' : 'background.paper',
                  border: '1px solid',
                  borderColor: optoutCount > 0 ? '#fde68a' : 'divider',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: optoutCount > 0 ? '#92400e' : 'text.secondary', fontWeight: 500, display: 'block' }}
                >
                  Opted Out
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: optoutCount > 0 ? '#b45309' : 'text.primary',
                  }}
                >
                  {optoutCount}
                </Typography>
              </Box>

              {duplicatesCount > 0 ? (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    minWidth: 80,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                    Duplicates
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#475569' }}>
                    {duplicatesCount}
                  </Typography>
                </Box>
              ) : null}

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  minWidth: 80,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}>
                  Queued
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'primary.main' }}>
                  {queuedCount}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.8125rem' }}>
              {optoutCount > 0
                ? `${optoutCount} opted-out ${optoutCount === 1 ? 'user has' : 'users have'} been excluded automatically. `
                : ''}
              {duplicatesCount > 0
                ? `${duplicatesCount} duplicate ${duplicatesCount === 1 ? 'number' : 'numbers'} skipped. `
                : ''}
              {eligibleCount === 0
                ? 'No eligible recipients found to send.'
                : `${eligibleCount} ${eligibleCount === 1 ? 'row is' : 'rows are'} ready. ${
                    send.scheduledAt
                      ? 'Press Schedule send to schedule this batch.'
                      : 'Press Send to start now.'
                  }`}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="primary"
                size="sm"
                loading={send.isUploadLoading}
                disabled={send.isUploadLoading || send.isDiscardLoading || eligibleCount === 0}
                onClick={send.sendDraft}
              >
                {send.scheduledAt ? 'Schedule send' : 'Send'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={send.isDiscardLoading}
                disabled={send.isUploadLoading || send.isDiscardLoading}
                onClick={send.discardDraft}
              >
                Remove queue
              </Button>
            </Box>
          </Alert>
        );
      })() : null}
      {send.job ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {send.job.status === 'scheduled'
            ? `Scheduled — ${counts.pending || 0} message(s) waiting to send. `
            : ''}
          {`Sent ${counts.sent || 0}, failed ${counts.failed || 0}, pending ${counts.pending || 0}, processing ${counts.processing || 0}`}
          {send.job.optout_count ? `, opted-out (excluded) ${send.job.optout_count}` : ''}
          <LinearProgress variant="determinate" value={send.sendProgress} sx={{ mt: 1 }} />
        </Alert>
      ) : null}
      {counts.failed > 0 ? (
        <Button
          variant="outline"
          size="sm"
          loading={send.isRetryLoading}
          disabled={send.isRetryLoading}
          onClick={send.retryFailed}
          sx={{ mt: 1 }}
        >
          Retry Failed
        </Button>
      ) : null}
      <Link component="button" variant="body2" onClick={send.downloadSampleFile} sx={{ mt: 2, display: 'inline-block' }}>
        Download sample file
      </Link>
    </Box>
  );
}
